import React from "react";
import { motion } from "framer-motion";
import { Linkedin } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";

const FOUNDER_PHOTO =
  "/assets/alessandro-di-rienzo-founder.png";

const Founder = () => {
  const { t } = useLang();
  return (
    <section id="founder" className="py-24 lg:py-32 bg-[var(--ivory-2)]" data-testid="founder-section">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="lg:col-span-5"
        >
          <div className="relative w-full max-w-[440px] mx-auto">
            <div className="absolute -top-4 -left-4 w-28 h-28 border border-[var(--champagne)]" aria-hidden />
            <div className="absolute -top-1 -left-1 right-1 bottom-1 bg-[var(--champagne)]/8" aria-hidden />
            <div className="relative bg-white border border-[var(--border-light)] overflow-hidden">
              <img
                src={FOUNDER_PHOTO}
                alt="Alessandro Di Rienzo"
                className="w-full h-auto aspect-[4/5] object-cover object-top grayscale-[20%] transition-all duration-700 hover:grayscale-0"
                data-testid="founder-photo"
              />
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[var(--navy)]/30 to-transparent pointer-events-none" />
            </div>
            <div className="absolute -bottom-4 -right-4 w-28 h-28 border border-[var(--navy)]/25" aria-hidden />
            <div className="absolute bottom-4 left-4 bg-[var(--ivory)] px-4 py-2 border border-[var(--border-light)]">
              <p className="overline" style={{ margin: 0 }}>Founder</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="lg:col-span-7"
        >
          <span className="overline" data-testid="founder-overline">{t.founder.overline}</span>
          <h2 className="font-serif text-[34px] sm:text-[42px] lg:text-[52px] leading-[1.08] tracking-tight text-[var(--navy)] mt-6" data-testid="founder-title">
            {t.founder.title}
          </h2>
          <p className="font-serif italic text-[22px] lg:text-[26px] text-[var(--champagne-hover)] mt-4" data-testid="founder-name">
            {t.founder.name}
          </p>
          <div className="mt-8 w-14 h-px bg-[var(--champagne)]" />
          <p className="mt-8 text-[16px] lg:text-[17px] leading-[1.85] text-[var(--charcoal)]" data-testid="founder-p1">
            {t.founder.p1}
          </p>
          <p className="mt-5 text-[16px] lg:text-[17px] leading-[1.85] text-[var(--charcoal)]" data-testid="founder-p2">
            {t.founder.p2}
          </p>
          <p className="mt-5 text-[16px] lg:text-[17px] leading-[1.85] text-[var(--charcoal)]" data-testid="founder-p3">
            {t.founder.p3}
          </p>

          <a
            href="https://www.linkedin.com/in/alessandro-di-rienzo/"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-10 inline-flex items-center gap-2 text-[13px] tracking-[0.16em] uppercase text-[var(--navy)] hover:text-[var(--champagne-hover)] transition-colors border-b border-[var(--border-light)] hover:border-[var(--champagne)] pb-1"
            data-testid="founder-linkedin"
          >
            <Linkedin className="w-4 h-4" strokeWidth={1.5} />
            LinkedIn — Alessandro Di Rienzo
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Founder;
